import { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  Menu, LogOut, User, Building2, ChevronDown, Repeat, Plus
} from "lucide-react"
import { useAuth } from "../auth/AuthContext"
import { api } from "../api/client"
import NotificationBell from "../notifications/NotificationBell.jsx"
import CreateCompanyModal from "../pages/MiEmpresa/_modals/CreateCompanyModal"

function CompanySwitcher() {
  const { companies, switchCompany, createCompany } = useAuth()
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [])

  // F11.72: mostrar selector aunque solo haya 1 empresa (para permitir crear mas)
  const hasCompanies = companies && companies.length > 0
  const active = hasCompanies ? (companies.find((c) => c.isActive) || companies[0]) : null

  return (
    <>
      <div ref={ref} className="relative hidden md:block">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-surface-200 hover:border-brand-300 hover:bg-brand-50/50 transition-all text-sm font-medium text-surface-700"
          >
            <Building2 className="w-4 h-4 text-brand" strokeWidth={2.2} />
            <span className="max-w-[160px] truncate">{active?.razonSocial || "Sin empresa"}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            title="Crear nueva empresa"
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-surface-200 hover:border-brand-300 hover:bg-brand-50/50 text-brand-600 transition-all"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-72 bg-white rounded-2xl border border-surface-200 shadow-float overflow-hidden z-50"
          >
            <div className="px-4 py-3 border-b border-surface-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
                Cambiar empresa
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto py-1.5">
              {companies.map((c) => (
                <button
                  key={c.id}
                  onClick={async () => {
                    setOpen(false)
                    if (!c.isActive) await switchCompany(c.id)
                  }}
                  className={[
                    "w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-brand-50 transition-colors",
                    c.isActive ? "bg-brand-50/60 text-brand-700 font-semibold" : "text-surface-700",
                  ].join(" ")}
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-brand flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{c.razonSocial}</div>
                    <div className="text-xs text-surface-400">RUC {c.ruc}</div>
                  </div>
                  {c.isActive && (
                    <div className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                  )}
                </button>
              ))}
            </div>
            <Link
              to="/select-company"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 border-t border-surface-100 text-sm font-semibold text-brand-600 hover:bg-brand-50 transition-colors"
            >
              <Repeat className="w-4 h-4" />
              Ver todas
            </Link>
            <button
              onClick={() => { setOpen(false); setCreateOpen(true) }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-surface-100 text-sm font-semibold text-brand-600 hover:bg-brand-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Crear nueva empresa
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      <CreateCompanyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        createCompany={createCompany} // FIX: Pasamos la funcion del contexto
        onCreated={(r) => {
          setCreateOpen(false)
        }}
      />
    </>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()
  const [avatarB64, setAvatarB64] = useState(null)

  useEffect(() => {
    if (!user?.dni) return
    let mounted = true
    fetch("/api/auth/me/reniec-photo", {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } // Asumimos token en LS
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (mounted && data?.foto) setAvatarB64(data.foto)
      })
      .catch(() => {})
    return () => { mounted = false }
  }, [user?.dni])
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [])

  const initials = (user?.fullName || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

  const roleLabel = user?.roleInCompany || user?.role || "usuario"

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-xl hover:bg-surface-100 transition-colors"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-brand flex items-center justify-center text-white text-sm font-bold shadow-card overflow-hidden border-2 border-white">
          {avatarB64 ? (
            <img src={`data:image/jpeg;base64,${avatarB64}`} alt="Avatar" className="w-full h-full object-cover rounded-full" />
          ) : (
            initials
          )}
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-semibold text-surface-800 max-w-[140px] truncate">
            {user?.fullName || user?.email}
          </div>
          <div className="text-[10px] text-surface-400 uppercase tracking-wide font-bold">
            {roleLabel}
          </div>
        </div>
        <ChevronDown className={`hidden md:block w-3.5 h-3.5 text-surface-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 bg-white rounded-2xl border border-surface-200 shadow-float overflow-hidden z-50"
          >
            <div className="px-4 py-3 border-b border-surface-100">
              <div className="text-sm font-semibold text-surface-800 truncate">{user?.fullName}</div>
              <div className="text-xs text-surface-400 truncate">{user?.email}</div>
            </div>
            <div className="py-1.5">
              <Link
                to="/perfil"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
              >
                <User className="w-4 h-4 text-surface-400" />
                Mi perfil
              </Link>
            </div>
            <div className="border-t border-surface-100 py-1.5">
              <button
                onClick={async () => {
                  setOpen(false)
                  await logout()
                  nav("/login")
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger/5 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function Topbar({ onToggleMobile, title }) {
  const [usdRate, setUsdRate] = useState(null);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await api.get("/api/exchange-rate/current");
        if (res && res.rate) setUsdRate(res.rate);
      } catch (e) {}
    };
    fetchRate();
    const int = setInterval(fetchRate, 60000 * 5); // Actualiza cada 5 min
    return () => clearInterval(int);
  }, []);

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-surface-200/60">
      <div className="flex items-center gap-4 h-16 px-4 sm:px-6">
        {/* Hamburger mobile */}
        <button
          onClick={onToggleMobile}
          className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-100 transition-colors overflow-hidden border-2 border-white shadow-sm"
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5 text-surface-700" />
        </button>

        {/* Título / Breadcrumb */}
        <div className="flex-1 min-w-0">
          {title && (
            <h1 className="font-display font-bold text-lg text-surface-900 truncate">
              {title}
            </h1>
          )}
        </div>

        {/* Empresa */}
        {usdRate && (
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-xl text-sm font-bold border border-emerald-200 shadow-sm">
            
            <span>1 USD = S/ {usdRate.toFixed(4)}</span>
          </div>
        )}
        <CompanySwitcher />

        {/* Notificaciones */}
        <NotificationBell />

        {/* User menu */}
        <UserMenu />
      </div>
    </header>
  )
}
